import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import { getContiguousRows } from '../../bom_generators/PerRowBomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../../MountingSystemAbstract'
import {
  getMlpeBrandDeviceCountsForSystem,
  getSystemFromBlocks,
} from '../../mlpeBlockUtils'
import { BomGeneratorContext, CompatibilityParameters, Item, MountingCalcResult, MountingComponentType } from '../../types'
import { addItems, createMountingItem } from '../../utils'
import { type AnchorType, anchorGroup, ANCHOR_SKU, type ClampStyle, type FenceAttachment, type FenceHeight, type PestProtection, normalizeAnchorType, RAIL_SKUS, type RailSize, selectClampSkus, selectGroundKitSku, selectScrews, SKU } from './FastRackCatalogue'
import { computeAnchorQty, computeBlockPerimeter_in, computeBlockThermalExpansionJoints, computeCgClipQty, computeClampCounts, computeEdgeClampQty, computeEdgeTrimPieces, computeEndCapQty, computeFenceRolls, computeJhookQty, computeLongSideLength_in, computeRowLength, computeShortSideLength_in, computeSpliceBreaks, getInterModuleGapMm, getModuleLengthAlongRail, getRails, INTER_MODULE_GAP_MM, panelMmToInches, parsePreferredRails, parsePreferredRailsFromOptions, type BlockPerimeterParams } from './FastRackComputations'
import { lookupFastRackTableSpanInches, floorSpanInches, resolveFastRackSnowWind } from './FastRackCalculatedSpan'
import { autoFetchDlubalCADataIfNeeded } from './FastRackDlubal'
import { applyPackConversionToEntries, applyPerimeterUplift, applySparePartsToEntries, consolidateBomEntries, type BomEntry } from './FastRackPostProcessors'
import { type Exposure, type MountType } from './FastRackSpanLookup'
import { FeatureFlagName } from 'util/featureFlags'
import { FAST_RACK_DEFAULT_COLOUR } from './FastRackInputForm'
import { MOUNT_FR } from './fastRackLabels'

export const FASTRACK_RT_MINI_SPAN_WARNING = 'FASTRACK_RT_MINI_SPAN_WARNING'
export const FASTRACK_SNOW_WARNING = 'FASTRACK_SNOW_WARNING'
export const FASTRACK_CLAMP_DEPTH_WARNING = 'FASTRACK_CLAMP_DEPTH_WARNING'
export const FASTRACK_PREFERRED_RAILS_WARNING = 'FASTRACK_PREFERRED_RAILS_WARNING'
export const FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING = 'FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING'

export const FAST_RACK_PREFERRED_RAILS_EMPTY_WARNING = MOUNT_FR.preferredRailsEmptyWarning

export function syncFastRackHiddenClampLandscapeWarning(
  systemUuid: string | undefined,
  hiddenClampValue: string,
  landscapeGridNames: string[]
): void {
  if (!systemUuid || !window.WorkspaceHelper) return

  if (hiddenClampValue === 'yes' && landscapeGridNames.length > 0) {
    const message = window.translate(MOUNT_FR.hiddenClampLandscapeInfo)
    window.WorkspaceHelper.addProjectErrorToReduxStore?.({
      message,
      key: FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING,
      severity: 'info',
      systemId: systemUuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  } else {
    window.WorkspaceHelper.removeProjectErrorFromReduxStore?.(
      FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING,
      systemUuid,
      'plugin'
    )
  }
}

export function syncFastRackPreferredRailsWarning(
  systemUuid: string | undefined,
  preferredRails: unknown,
  translate?: (key: string) => string
): void {
  if (!systemUuid || !window.WorkspaceHelper) return

  const sizes = parsePreferredRailsFromOptions(preferredRails)
  if (sizes.length === 0) {
    const message =
      translate?.(FAST_RACK_PREFERRED_RAILS_EMPTY_WARNING) ??
      window.translate?.(FAST_RACK_PREFERRED_RAILS_EMPTY_WARNING) ??
      FAST_RACK_PREFERRED_RAILS_EMPTY_WARNING
    window.WorkspaceHelper.addProjectErrorToReduxStore?.({
      message,
      key: FASTRACK_PREFERRED_RAILS_WARNING,
      severity: 'warning',
      systemId: systemUuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  } else {
    window.WorkspaceHelper.removeProjectErrorFromReduxStore?.(
      FASTRACK_PREFERRED_RAILS_WARNING,
      systemUuid,
      'plugin'
    )
  }
}

export const RT_MINI_SPAN_USER_LOWER_INFO = MOUNT_FR.rtMiniSpanUserLower

export const RT_MINI_SPAN_CALCULATED_LOWER_INFO = MOUNT_FR.rtMiniSpanCalcLower

function rtMiniSpanMessageVars(userRounded: number, tableRounded: number) {
  return { userSpan: String(userRounded), tableSpan: String(tableRounded) }
}

function translateRtMiniSpanMessage(
  key: string,
  vars: ReturnType<typeof rtMiniSpanMessageVars>
): string {
  return window.translate?.(key, vars) ?? key.replace('%{userSpan}', vars.userSpan).replace('%{tableSpan}', vars.tableSpan)
}

export function shouldShowRtMiniSpanInfo(userSpan_in: number, tableSpan_in: number): boolean {
  return floorSpanInches(userSpan_in) !== floorSpanInches(tableSpan_in)
}

export function formatRtMiniSpanInfoMessage(userSpan_in: number, tableSpan_in: number): string {
  const tableRounded = floorSpanInches(tableSpan_in)
  const userRounded = floorSpanInches(userSpan_in)
  const vars = rtMiniSpanMessageVars(userRounded, tableRounded)
  if (userRounded < tableRounded) {
    return translateRtMiniSpanMessage(RT_MINI_SPAN_USER_LOWER_INFO, vars)
  }
  return translateRtMiniSpanMessage(RT_MINI_SPAN_CALCULATED_LOWER_INFO, vars)
}

export function parseSparePercent(value: string): number {
  if (value === '10' || value === '10%') return 10
  if (value === '5' || value === '5%') return 5
  return 0
}

function emitItems(code: string, qty: number, type: MountingComponentType, blockIndex?: number): Item[] {
  const items: Item[] = []
  const template: Item = {
    name: code,
    components: [{ name: code, type, left: 0, top: 0 }],
  }
  for (let i = 0; i < qty; i++) {
    items.push(createMountingItem(template, { blockIndex }))
  }
  return items
}

class FastRackLayoutAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet: SpacingRuleSet = {
    interPanel: { horizontalSpacing: INTER_MODULE_GAP_MM, verticalSpacing: INTER_MODULE_GAP_MM },
  }
}

interface FastRackBomBuckets {
  rows: BomEntry[][]
  rowRails: BomEntry[][]
  mlpe: BomEntry[]
  accessories: BomEntry[]
  railTotals: BomEntry[]
  pest: BomEntry[]
}

function createBomBuckets(rowCount: number): FastRackBomBuckets {
  return {
    rows: Array.from({ length: rowCount }, () => []),
    rowRails: Array.from({ length: rowCount }, () => []),
    mlpe: [],
    accessories: [],
    railTotals: [],
    pest: [],
  }
}

function flattenBomBuckets(buckets: FastRackBomBuckets): BomEntry[] {
  return [
    ...buckets.rows.flat(),
    ...buckets.mlpe,
    ...buckets.accessories,
    ...buckets.railTotals,
    ...buckets.pest,
  ]
}

class FastRackBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult, _context?: BomGeneratorContext): Promise<MountingCalcResult> {
    await autoFetchDlubalCADataIfNeeded(this.input.options)

    const options = this.input.options
    const clampStyle = ((options.clampStyle as string) || 'clickLock') as ClampStyle
    const colour = (options.componentColour as string) || FAST_RACK_DEFAULT_COLOUR
    const isBlockLandscape = this.block.orientation === 'landscape'
    const hiddenClamp = options.hiddenClamp !== 'no' && !isBlockLandscape
    const frameDepth = this.block.panel.thickness ?? 35
    const preferredRails = parsePreferredRails((options.preferredRails as string) || 'all')
    const sparePercent = parseSparePercent((options.sparePartsPercent as string) ?? '')

    if (isBlockLandscape && options.hiddenClamp !== 'no') {
      this.emitInfo(
        FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING,
        window.translate(MOUNT_FR.hiddenClampLandscapeInfo)
      )
    }

    if (preferredRails.length === 0) {
      this.emitWarning(FASTRACK_PREFERRED_RAILS_WARNING, FAST_RACK_PREFERRED_RAILS_EMPTY_WARNING)
    } else {
      this.clearWarning(FASTRACK_PREFERRED_RAILS_WARNING)
    }

    const bi = this.blockIndex
    const moduleLength = getModuleLengthAlongRail(this.block)
    const interModuleGap_mm = getInterModuleGapMm(this.block)
    const anchor = normalizeAnchorType((options.anchorType as string) || ANCHOR_SKU.TALON_XE)
    const mountType = ((options.mountType as string) || 'SHEATHING') as MountType

    if (anchor !== ANCHOR_SKU.RT_MINI_BOX_20) {
      this.clearWarning(FASTRACK_RT_MINI_SPAN_WARNING)
    }

    const structural = this.resolveStructuralInputs(anchor, mountType, moduleLength)
    if (structural.aborted) {
      return result
    }
    const { span_in, pitchDistance_in } = structural

    const clampSkus = selectClampSkus(clampStyle, hiddenClamp, frameDepth, colour)
    if (clampSkus.error) {
      this.emitWarning(FASTRACK_CLAMP_DEPTH_WARNING, clampSkus.error)
      return result
    }
    this.clearWarning(FASTRACK_CLAMP_DEPTH_WARNING)

    const clickLockNonHidden = clampStyle === 'clickLock' && !hiddenClamp
    const useHiddenEndClamps = hiddenClamp

    const contiguousRows = this.block.rows.flatMap(getContiguousRows)
    const buckets = createBomBuckets(contiguousRows.length)

    const pushRow = (rowIndex: number, sku: string, qty: number, type: MountingComponentType) => {
      if (qty <= 0) return
      buckets.rows[rowIndex].push({ sku, qty, type })
    }

    const pushRowRail = (rowIndex: number, sku: string, qty: number) => {
      if (qty <= 0) return
      buckets.rowRails[rowIndex].push({ sku, qty, type: MountingComponentType.RAIL })
    }

    const pushMlpe = (sku: string, qty: number, type: MountingComponentType) => {
      if (qty <= 0) return
      buckets.mlpe.push({ sku, qty, type })
    }

    const pushAccessory = (sku: string, qty: number, type: MountingComponentType) => {
      if (qty <= 0) return
      buckets.accessories.push({ sku, qty, type })
    }

    const pushRailTotal = (sku: string, qty: number) => {
      if (qty <= 0) return
      buckets.railTotals.push({ sku, qty, type: MountingComponentType.RAIL })
    }

    const railTotals: Partial<Record<RailSize, number>> = {}

    contiguousRows.forEach((row, rowIndex) => {
      const numPanels = row.length
      if (numPanels === 0) return

      const rowLen = computeRowLength(numPanels, moduleLength, useHiddenEndClamps, interModuleGap_mm)
      const spliceBreaks = computeSpliceBreaks(rowLen)
      const clamps = computeClampCounts(numPanels, spliceBreaks, clickLockNonHidden)

      pushRow(rowIndex, clampSkus.midClampSku, clamps.midClamps, MountingComponentType.CLAMP)

      if (clickLockNonHidden) {
        if (clampSkus.stopperSku) {
          pushRow(rowIndex, clampSkus.stopperSku, clamps.stoppers, MountingComponentType.CLAMP)
        }
      } else {
        pushRow(rowIndex, clampSkus.endClampSku, clamps.endClamps, MountingComponentType.CLAMP)
      }

      pushRow(rowIndex, SKU.BONDING_JUMPER, clamps.bondingJumpers, MountingComponentType.OTHER)

      const railResult = getRails(rowLen, preferredRails)
      if (railResult) {
        for (const [size, count] of Object.entries(railResult.counts)) {
          const railSize = Number(size) as RailSize
          const perRun = count ?? 0
          const totalForRow = perRun * 2
          railTotals[railSize] = (railTotals[railSize] ?? 0) + totalForRow
          pushRowRail(rowIndex, RAIL_SKUS[railSize], totalForRow)
        }
        if (railResult.pieces > 1) {
          pushRow(rowIndex, SKU.SPLICE, (railResult.pieces - 1) * 2, MountingComponentType.SPLICE)
        }
      }

      if (span_in > 0) {
        const { anchorQty, footKitQty } = computeAnchorQty(anchor, rowLen, span_in, pitchDistance_in)
        pushRow(rowIndex, anchor, anchorQty, MountingComponentType.ROOF_HOOK)
        pushRow(rowIndex, SKU.FOOT_KIT, footKitQty, MountingComponentType.OTHER)

        const screwSpec = selectScrews(anchor, mountType)
        if (screwSpec) {
          pushRow(rowIndex, screwSpec.sku, anchorQty * screwSpec.qtyPerAnchor, MountingComponentType.OTHER)
        }
      }
    })

    const nonEmptyRows = contiguousRows.filter((r) => r.length > 0)
    pushMlpe(SKU.GROUND_LUG, nonEmptyRows.length * 2, MountingComponentType.OTHER)

    if (options.endCaps !== 'no' && options.endCaps !== false) {
      const totalSpliceBreaks = contiguousRows.reduce(
        (sum, row) =>
          sum + computeSpliceBreaks(computeRowLength(row.length, moduleLength, useHiddenEndClamps, interModuleGap_mm)),
        0
      )
      pushAccessory(SKU.END_CAP, computeEndCapQty(nonEmptyRows.length, totalSpliceBreaks), MountingComponentType.OTHER)
    }

    const pestProtection = ((options.pestProtection as string) || 'none') as PestProtection
    if (pestProtection !== 'none') {
      this.addPestProtection(buckets.pest, pestProtection, sparePercent, useHiddenEndClamps, moduleLength, contiguousRows)
    }

    for (const [size, count] of Object.entries(railTotals)) {
      const railSize = Number(size) as RailSize
      const sku = RAIL_SKUS[railSize]
      if (sku && count) {
        pushRailTotal(sku, count)
      }
    }

    const bom = flattenBomBuckets(buckets)
    const consolidatedBom = consolidateBomEntries(bom)

    const items: Item[] = []
    for (const entry of consolidatedBom) {
      items.push(...emitItems(entry.sku, entry.qty, entry.type, bi))
    }

    return addItems(result, items)
  }


  private resolveStructuralInputs(
    anchor: AnchorType,
    mountType: MountType,
    moduleLength_mm: number
  ): { span_in: number; pitchDistance_in: number; aborted: boolean } {
    const { snow_kPa } = this.resolveSnowWind()

    if (snow_kPa !== null && snow_kPa > 4.0) {
      this.emitWarning(FASTRACK_SNOW_WARNING, window.translate(MOUNT_FR.snowExceedsWarning))
      return { span_in: 0, pitchDistance_in: 0, aborted: true }
    }
    this.clearWarning(FASTRACK_SNOW_WARNING)

    const span_in = this.resolveSpan(anchor, mountType, moduleLength_mm)
    const pitchDistance_in = this.resolvePitchDistance(anchor)
    return { span_in, pitchDistance_in, aborted: false }
  }

  /**
   * Resolve the effective span (inches) for anchor qty calculation.
   * - Talon: from span table lookup (Dlubal + CSV)
   * - RT-MINI: lesser of calculated table span and user-entered rtMiniCalcSpan
   * - Metal: same span table as Talon (pitch snapping handled in computeAnchorQty)
   */
  private resolveSpan(anchor: AnchorType, mountType: MountType, moduleLength_mm: number): number {
    const tableSpan_in = this.lookupTableSpan(mountType, moduleLength_mm)

    if (anchor === ANCHOR_SKU.RT_MINI_BOX_20) {
      const userSpan_in = floorSpanInches(Number(this.input.options.rtMiniCalcSpan) || 48)

      if (tableSpan_in === null) {
        this.clearWarning(FASTRACK_RT_MINI_SPAN_WARNING)
        return userSpan_in
      }

      const tableRounded = floorSpanInches(tableSpan_in)
      const userRounded = userSpan_in

      if (!shouldShowRtMiniSpanInfo(userSpan_in, tableSpan_in)) {
        this.clearWarning(FASTRACK_RT_MINI_SPAN_WARNING)
        return userSpan_in
      }

      this.emitInfo(FASTRACK_RT_MINI_SPAN_WARNING, formatRtMiniSpanInfoMessage(userSpan_in, tableSpan_in))
      if (userRounded < tableRounded) {
        return userSpan_in
      }
      return tableRounded
    }

    if (tableSpan_in === null) {
      return 48
    }
    return tableSpan_in
  }

  private lookupTableSpan(mountType: MountType, moduleLength_mm: number): number | null {
    const moduleGrid = this.block.uuid ? window.editor?.objectByUuid(this.block.uuid) : null
    const slopeDeg = moduleGrid?.getSlope?.() ?? 20
    const { snow_kPa, wind_q150_kPa } = resolveFastRackSnowWind(
      this.input.options as Record<string, unknown>,
      slopeDeg
    )
    const exposure = ((this.input.options.exposure as string) || 'Rough') as Exposure

    return lookupFastRackTableSpanInches({
      mountType,
      moduleLength_mm,
      slopeDeg,
      exposure,
      snow_kPa,
      wind_q150_kPa,
    })
  }

  private resolvePitchDistance(anchor: AnchorType): number {
    const group = anchorGroup(anchor)
    if (group === 'standingSeam') return Number(this.input.options.seamSpacing) || 9
    if (group === 'corrugatedMetal') return Number(this.input.options.majorRibSpacing) || 9
    return 0
  }

  private getSystemUuid(): string | undefined {
    return getSystemFromBlocks([this.block])?.uuid
  }

  private emitWarning(key: string, message: string): void {
    window.WorkspaceHelper?.addProjectErrorToReduxStore?.({
      message,
      key,
      severity: 'warning',
      systemId: this.getSystemUuid(),
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  private emitInfo(key: string, message: string): void {
    window.WorkspaceHelper?.addProjectErrorToReduxStore?.({
      message,
      key,
      severity: 'info',
      systemId: this.getSystemUuid(),
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  private clearWarning(key: string): void {
    const systemId = this.getSystemUuid()
    if (systemId) {
      window.WorkspaceHelper?.removeProjectErrorFromReduxStore?.(key, systemId, 'plugin')
    }
  }

  private resolveSnowWind(): { snow_kPa: number | null; wind_q150_kPa: number | null } {
    const moduleGrid = this.block.uuid ? window.editor?.objectByUuid(this.block.uuid) : null
    const slopeDeg = moduleGrid?.getSlope?.() ?? 20
    return resolveFastRackSnowWind(this.input.options as Record<string, unknown>, slopeDeg)
  }

  /**
   * Add pest protection BOM entries. Perimeter uplift is applied before
   * computing fence/trim quantities (spare % on perimeter, not qty).
   */
  private addPestProtection(
    pestOut: BomEntry[],
    pestType: PestProtection,
    sparePercent: number,
    useHiddenEndClamps: boolean,
    moduleLength_mm: number,
    contiguousRows: ReturnType<typeof getContiguousRows>
  ): void {
    const panel = this.block.panel
    const isLandscape = this.block.orientation === 'landscape'
    const panelWidth_in = panelMmToInches(isLandscape ? panel.height : panel.width)
    const panelHeight_in = panelMmToInches(isLandscape ? panel.width : panel.height)

    const numRows = this.block.rows.length
    const numCols = this.block.rows[0]?.length ?? 0
    const shortSides = 2 * numCols
    const longSides = 2 * numRows

    const thermalExpansionJoints = computeBlockThermalExpansionJoints(
      contiguousRows.map((row) => row.length),
      moduleLength_mm,
      useHiddenEndClamps,
      getInterModuleGapMm(this.block)
    )

    const perimeterParams: BlockPerimeterParams = {
      panelWidth_in,
      panelHeight_in,
      shortSides,
      longSides,
      useHiddenEndClamps,
      thermalExpansionJoints,
    }

    const rawPerimeter_in = computeBlockPerimeter_in(perimeterParams)
    if (rawPerimeter_in <= 0) return

    const perimeter_in = applyPerimeterUplift(rawPerimeter_in, sparePercent)
    const longSideLength_in = applyPerimeterUplift(computeLongSideLength_in(perimeterParams), sparePercent)
    const shortSideLength_in = applyPerimeterUplift(computeShortSideLength_in(perimeterParams), sparePercent)

    const addPest = (sku: string, qty: number) => {
      if (qty > 0) {
        pestOut.push({ sku, qty, type: MountingComponentType.OTHER })
      }
    }

    if (pestType === 'critterGuard') {
      const fenceHeight = ((this.input.options.fenceHeight as string) || '6') as FenceHeight
      const fenceSku = fenceHeight === '8' ? SKU.FENCE_8 : SKU.FENCE_6
      addPest(fenceSku, computeFenceRolls(perimeter_in))

      const attachment = ((this.input.options.fenceAttachment as string) || 'jhooksCgClips') as FenceAttachment

      if (attachment === 'edgeClamps') {
        addPest(SKU.EDGE_CLAMP, computeEdgeClampQty(perimeter_in))
      } else {
        addPest(SKU.JHOOK, computeJhookQty(longSideLength_in))
        if (attachment === 'jhooksCgClips') {
          addPest(SKU.CG_CLIP, computeCgClipQty(shortSides))
        } else {
          addPest(SKU.EDGE_CLAMP, computeEdgeClampQty(shortSideLength_in))
        }
      }
    } else if (pestType === 'edgeTrim') {
      const trimPieces = computeEdgeTrimPieces(perimeter_in)
      addPest(SKU.EDGE_TRIM, trimPieces)
      addPest(SKU.EDGE_CLAMP, 2 * trimPieces)
    }
  }
}

export class FastRack extends MountingSystemAbstract {
  layoutAdjusterClass = FastRackLayoutAdjuster
  bomGeneratorClass = FastRackBomGenerator

  applyMountingOptions(options: Record<string, unknown>): void {
    this.input.options = { ...this.input.options, ...options }
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      slopeRange: [7, 45],
      relativeTiltsAllowedValues: [0],
      roofTypes: ['Composition / Asphalt Shingle', 'Metal Standing Seam', 'Metal Tin'],
      roofTypeRequired: true,
      featureFlag: 'mounting_fastrack' as FeatureFlagName,
    }
  }

  /**
   * Override to consolidate per-block items within a single roof facet.
   * Spare parts and pack conversion are applied AFTER all roof facets are
   * merged — see {@link applySystemWidePostProcessing}.
   */
  async generateBom(result: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    const rawResult = await super.generateBom(result, context)

    const entryMap = new Map<string, BomEntry>()
    for (const item of rawResult.items) {
      if (!item.name) continue
      const type = item.components[0]?.type ?? MountingComponentType.OTHER
      const existing = entryMap.get(item.name)
      if (existing) {
        existing.qty += 1
      } else {
        entryMap.set(item.name, { sku: item.name, qty: 1, type })
      }
    }

    const consolidated = Array.from(entryMap.values())

    const items: Item[] = []
    for (const entry of consolidated) {
      items.push(...emitItems(entry.sku, entry.qty, entry.type))
    }

    return { ...rawResult, items }
  }

  /**
   * Apply spare parts uplift and pack conversion once across the merged
   * system-wide items (all roof facets combined). Called from calculate.ts
   * after all roof results are merged.
   */
  static applySystemWidePostProcessing(
    items: Item[],
    options: Record<string, unknown>,
    system?: import('types/global').StudioSystemType
  ): Item[] {
    const sparePercent = parseSparePercent((options.sparePartsPercent as string) ?? '')
    const groundKitSkus = new Set<string>([SKU.GROUND_KIT_16, SKU.GROUND_KIT_20])

    const entryMap = new Map<string, BomEntry>()
    for (const item of items) {
      if (!item.name || groundKitSkus.has(item.name)) continue
      const type = item.components[0]?.type ?? MountingComponentType.OTHER
      const existing = entryMap.get(item.name)
      if (existing) {
        existing.qty += 1
      } else {
        entryMap.set(item.name, { sku: item.name, qty: 1, type })
      }
    }

    if (system) {
      for (const { manufacturer, quantity } of getMlpeBrandDeviceCountsForSystem(system)) {
        if (quantity <= 0) continue
        const groundKitSku = selectGroundKitSku(manufacturer) ?? SKU.GROUND_KIT_20
        const existing = entryMap.get(groundKitSku)
        if (existing) {
          existing.qty += quantity
        } else {
          entryMap.set(groundKitSku, { sku: groundKitSku, qty: quantity, type: MountingComponentType.OTHER })
        }
      }
    }

    entryMap.delete(SKU.CABLE_CLIP)
    const cableClipQty = Number(options.cableClipQty) || 0
    if (cableClipQty > 0) {
      entryMap.set(SKU.CABLE_CLIP, {
        sku: SKU.CABLE_CLIP,
        qty: cableClipQty,
        type: MountingComponentType.OTHER,
      })
    }

    const consolidated = Array.from(entryMap.values())
    const afterSpare = applySparePartsToEntries(consolidated, sparePercent)
    const processed = applyPackConversionToEntries(afterSpare)

    const result: Item[] = []
    for (const entry of processed) {
      result.push(...emitItems(entry.sku, entry.qty, entry.type))
    }
    return result
  }
}
